import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useCall } from '@/features/calls/context/CallContext';
import {
  APP_UPDATE_POLL_MS,
  canApplyUpdate,
  currentBundleSignature,
  fetchDeployedBundleSignature,
  markReloaded,
} from '@/lib/appUpdate';

/**
 * Silent app self-updater. Polls the deployed index.html and, when a new
 * bundle is live, reloads the page at the first safe moment: on a route
 * change, or while the tab is hidden — never during a call and never twice
 * within the cooldown window. Renders nothing.
 *
 * Must be mounted inside BrowserRouter (useLocation) and CallProvider
 * (useCall). Inert on the Vite dev server, where no hashed bundle exists.
 */
export function AppUpdateWatcher() {
  const { callState } = useCall();
  const location = useLocation();

  const updateReadyRef = useRef(false);
  const callStateRef = useRef(callState);
  callStateRef.current = callState;

  // ── Detection ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const current = currentBundleSignature();
    if (!current) return; // dev server or unexpected markup: stay inert

    let cancelled = false;

    const check = async () => {
      if (cancelled || updateReadyRef.current) return;
      const deployed = await fetchDeployedBundleSignature();
      if (cancelled || !deployed || deployed === current) return;
      updateReadyRef.current = true;
      // A hidden tab fires no route changes and may sit for hours: apply the
      // pending update straight away if we're allowed to.
      if (canApplyUpdate({ callState: callStateRef.current, navigating: false, hidden: document.hidden })) {
        markReloaded();
        window.location.reload();
      }
    };

    const interval = window.setInterval(() => void check(), APP_UPDATE_POLL_MS);
    const onVisibility = () => {
      // Tab going hidden: safest possible moment to swap the app out.
      // Tab coming back: re-check so stale tabs catch up right away.
      void check();
      if (
        updateReadyRef.current &&
        canApplyUpdate({ callState: callStateRef.current, navigating: false, hidden: document.hidden })
      ) {
        markReloaded();
        window.location.reload();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // ── Apply on navigation ────────────────────────────────────────────────────
  // The router has already committed the new URL, so reloading here lands the
  // user exactly where they were going, just on the fresh bundle.
  useEffect(() => {
    if (!updateReadyRef.current) return;
    if (!canApplyUpdate({ callState: callStateRef.current, navigating: true, hidden: document.hidden })) return;
    markReloaded();
    window.location.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}
